/**
 * Colour roles shared by both apps (webapp design system, MR-1).
 *
 * Every value here is the colour of a mobile token today
 * (`apps/mobile/src/theme/index.tsx` + `colors/base.tsx`), named by the role it
 * plays instead of by the mobile key. The webapp theme reads them from here;
 * mobile keeps its own copies for now and adopts this module later, with no
 * visual change because the colours are the same.
 * `__tests__/theme/colorRoles.test.ts` reads the mobile source and fails the
 * moment the two drift.
 *
 * Why hex crosses the core seam here, against the usual guard rail: these are
 * not one screen's styling, they are the brand and weather-family palette
 * whose whole point is that both apps paint the same thing. The surfaces,
 * lines and text greys are NOT here — those are webapp-specific
 * (`apps/webapp/src/theme/tokens.ts`).
 *
 * Use rules (from the signed-off spec, section 1):
 * - `brand.fill` is a FILL only: filled-button background, avatar, nav
 *   selection bar, brand marks. Never text, never a state indicator — it is
 *   2.36:1 on the light card. `brand.onFill` is the label on it.
 * - `ink.main` is the brand blue as an ink: links, text buttons, tab
 *   indicator, focus ring, switch-on, selected chip, radio. `ink.onInk` is the
 *   label or thumb on an ink fill.
 * - `*.tint` fills a chip, banner or band; `*.onTint` draws the glyph and text
 *   on it. A family's `on` is also its chart line colour. Neither is ever
 *   used for a measured value, which is always `text.primary`.
 */

export type ThemeName = 'light' | 'dark';

export type WeatherFamily =
  'temperature' | 'rain' | 'wind' | 'solar' | 'overview' | 'agro';

export type TintPair = {readonly tint: string; readonly onTint: string};

export type FamilyRole = {readonly tint: string; readonly on: string};

export type ColorRoles = {
  readonly brand: {readonly fill: string; readonly onFill: string};
  readonly ink: {readonly main: string; readonly onInk: string};
  readonly danger: TintPair & {readonly main: string; readonly onMain: string};
  readonly success: TintPair;
  readonly info: TintPair;
  readonly warning: TintPair;
  readonly premium: TintPair;
  readonly family: {readonly [F in WeatherFamily]: FamilyRole};
  /** The year day-length curve, shortest day to longest. */
  readonly dayCurve: {
    readonly low: string;
    readonly mid: string;
    readonly high: string;
  };
  /**
   * A record mark on a chart: the record high and the record low (On this
   * day in history, FE #94). Graphics, 3:1 on the card in both themes; the
   * value beside a mark is still drawn in the text colour.
   */
  readonly record: {readonly max: string; readonly min: string};
};

const BRAND_BLUE = '#33B5E5';

const deepFreeze = <T extends object>(value: T): T => {
  Object.values(value).forEach((child) => {
    if (child && typeof child === 'object') {
      deepFreeze(child);
    }
  });
  return Object.freeze(value);
};

// Mobile sources: brand.fill = primary, brand.onFill = onPrimaryFill,
// ink.main = primaryInk, ink.onInk = onPrimary (dark), danger = error / onError
// / dangerFill / dangerOn, success = successFill / successOn, info =
// accentRain(On), warning = accentSolar(On), premium = premiumContainer /
// premiumInk, family.* = accent*(On), dayCurve = dayCurveLow/Mid/High,
// record = maxValue / minValue.
export const colorRoles: {readonly [T in ThemeName]: ColorRoles} = deepFreeze({
  light: {
    brand: {fill: BRAND_BLUE, onFill: '#191c1e'},
    ink: {main: '#0c6285', onInk: '#ffffff'},
    danger: {
      main: '#ba1a1a',
      onMain: '#ffffff',
      tint: '#eddde0',
      onTint: '#410002',
    },
    success: {tint: '#d3e4dc', onTint: '#2b6a2b'},
    info: {tint: '#d1e8f1', onTint: '#186d93'},
    warning: {tint: '#ebe7d5', onTint: '#8b5f00'},
    premium: {tint: '#fae5ea', onTint: '#c1102f'},
    family: {
      temperature: {tint: '#efe3da', on: '#a74b0a'},
      rain: {tint: '#d1e8f1', on: '#186d93'},
      wind: {tint: '#d3e9ea', on: '#22706b'},
      solar: {tint: '#ebe7d5', on: '#8b5f00'},
      overview: {tint: '#dadfe7', on: '#5e5b7d'},
      agro: {tint: '#d7e8e0', on: '#397238'},
    },
    dayCurve: {low: '#195970', mid: '#227797', high: '#2a96be'},
    record: {max: '#ba1a1a', min: '#5e5b7d'},
  },
  dark: {
    brand: {fill: BRAND_BLUE, onFill: '#00344e'},
    // On dark the brand blue already clears 7.46:1 on the card, so the ink IS
    // the fill. The focus ring therefore needs its 2px offset to be visible
    // around a filled button at all (1.00:1 against it).
    ink: {main: BRAND_BLUE, onInk: '#00344e'},
    danger: {
      main: '#ffb4ab',
      onMain: '#690005',
      tint: '#513838',
      onTint: '#ffb4ab',
    },
    success: {tint: '#304237', onTint: '#79c878'},
    info: {tint: '#2b4551', onTint: '#57c7f0'},
    warning: {tint: '#4b4430', onTint: '#f5be4a'},
    premium: {tint: '#402127', onTint: '#ff8fa2'},
    family: {
      temperature: {tint: '#4d3b35', on: '#ff9463'},
      rain: {tint: '#2b4551', on: '#57c7f0'},
      wind: {tint: '#2d4647', on: '#5fc9c1'},
      solar: {tint: '#4b4430', on: '#f5be4a'},
      overview: {tint: '#3b3f4c', on: '#a9a5d9'},
      agro: {tint: '#324639', on: '#79c878'},
    },
    dayCurve: {low: '#227797', mid: '#2a96be', high: BRAND_BLUE},
    record: {max: '#ffb4ab', min: '#55d6f4'},
  },
});
